import {useSafeChatStateLens} from '@github-ui/copilot-chat/CopilotChatContext'
import {useSafeChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useSafeGetChatState} from '@github-ui/copilot-chat-contexts/ObservableChatContext'
import {isFileReference, referencesAreEqual} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useOptionalLayoutContext} from '@github-ui/pacer/Layout/layout-context'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ScrollContainer} from '@github-ui/scroll-container'
import {usePreviousValue} from '@github-ui/use-previous-value'
import {FocusKeys} from '@primer/behaviors'
import {
  FileZipIcon,
  KebabHorizontalIcon,
  ListUnorderedIcon,
  MaximizeIcon,
  MinimizeIcon,
  XIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, useFocusZone} from '@primer/react'
import {downloadZip} from 'client-zip'
import {clsx} from 'clsx'
import {startTransition, useCallback, useEffect, useId, useLayoutEffect, useRef} from 'react'

import {ErrorFallback} from '../ErrorFallback'
import {getImplicitReferenceForItem} from './content-preview-types'
import styles from './ContentPreview.module.css'
import {
  type ContentPreviewComponentProps,
  type ContentPreviewExpansionProps,
  hasControlledContentPreviewExpansion,
} from './ContentPreviewComponentContext'
import {
  useContentPreview,
  useContentPreviewOpen,
  useResourcePanelOpen,
  useWasAutoOpenedWithoutFocus,
} from './ContentPreviewContext'
import {ContentPreviewItem} from './ContentPreviewItem'
import {ContentPreviewTab} from './ContentPreviewTab'
import {downloadFile} from './download-file'
import {EmptyState} from './EmptyState'
import {LoadingState} from './LoadingState'
import {sendContentPreviewEvent} from './telemetry'

/** Hook to close preview pane and collapse secondary column if expanded */
function useClosePreviewPane(expansionProps: ContentPreviewExpansionProps) {
  const {closePreviewPane} = useContentPreview()
  const layoutContext = useOptionalLayoutContext()
  const secondaryColumnFullWidth = layoutContext?.secondaryColumnFullWidth ?? false
  const hostControlsExpansion = hasControlledContentPreviewExpansion(expansionProps)
  const onExpandedChange = hostControlsExpansion ? expansionProps.onExpandedChange : null
  // Destructured because the pacer Layout context value is a fresh object every render, so
  // depending on the object itself would make this callback unstable.
  const toggleSecondaryColumnFullWidth = layoutContext?.toggleSecondaryColumnFullWidth
  const setDesktopLayoutState = layoutContext?.setDesktopLayoutState

  return useCallback(() => {
    if (onExpandedChange) {
      onExpandedChange(false)
    } else if (secondaryColumnFullWidth) {
      toggleSecondaryColumnFullWidth?.()
      setDesktopLayoutState?.(prev => ({...prev, fullWidth: false}))
    }
    closePreviewPane()
  }, [
    closePreviewPane,
    onExpandedChange,
    secondaryColumnFullWidth,
    toggleSecondaryColumnFullWidth,
    setDesktopLayoutState,
  ])
}

function ContentPreviewInner(props: ContentPreviewComponentProps) {
  const {
    returnFocusRef,
    disableResourcePanel = false,
    resourcePanelSupportedOverride,
    disableActionsMenu = false,
    hideExpandButton = false,
    hideIcebreakerSuggestion = false,
  } = props
  const isOpen = useContentPreviewOpen()
  const isResourcePanelOpen = useResourcePanelOpen()
  // Selective subscription: a full-state read would re-render the workbench on every streamed token.
  const selectedThreadID = useSafeChatStateLens(s => s?.selectedThreadID)
  const optedInToPreviewFeatures = useSafeChatStateLens(s => s?.optedInToPreviewFeatures)
  const manager = useSafeChatManager()
  const getChatState = useSafeGetChatState()
  const prevSelectedThreadID = usePreviousValue(selectedThreadID)
  const {
    items,
    allOpenItems: openItems,
    selectedItem,
    openItem,
    closeItem,
    closeItems,
    showLoadingState,
    openResourcePanel,
    returnFocusElementRef,
    ephemeralTab,
    promoteTab,
  } = useContentPreview()
  const handleClosePreviewPane = useClosePreviewPane(props)
  const prevIsOpen = usePreviousValue(isOpen)
  const isOpeningTransition = isOpen && !prevIsOpen
  const wasAutoOpenedWithoutFocus = useWasAutoOpenedWithoutFocus(isOpeningTransition)
  const isOpening = isOpeningTransition && !wasAutoOpenedWithoutFocus
  const isClosing = prevIsOpen && !isOpen
  const openFiles = openItems
    .map(itemId => items.get(itemId))
    .filter(item => item?.type === 'file')
    .filter(item => !item.unavailableReason)

  const activeTabId = useId()
  const selectedTabPanelId = useId()
  const closeTabControlHintId = useId()

  // Pinned items (e.g. a task's always-present diff tab) render first, ahead of any other open
  // items; Array.prototype.sort is stable, so relative order within each group is preserved.
  const openItemInstances = openItems
    .map(key => items.get(key))
    .filter(item => !!item)
    .sort((a, b) => Number(b.pinned ?? false) - Number(a.pinned ?? false))
  const selectedItemInstance = openItemInstances.find(item => item.id === selectedItem) ?? openItemInstances[0]
  const previousSelectedItem = usePreviousValue(selectedItem)

  const resourcePanelSupported = (resourcePanelSupportedOverride ?? optedInToPreviewFeatures) && !disableResourcePanel
  useEffect(() => {
    if (isClosing) {
      // Use the stored returnFocusElement if available, otherwise fall back to returnFocusRef
      const returnFocusElement = returnFocusElementRef.current
      if (returnFocusElement) {
        returnFocusElement.focus()
      } else {
        returnFocusRef.current?.focus()
      }
    }
  }, [isClosing, returnFocusRef, returnFocusElementRef])

  useEffect(() => {
    // close preview pane when changing threads
    if (prevSelectedThreadID != null && prevSelectedThreadID !== selectedThreadID) {
      handleClosePreviewPane()
    }
  }, [handleClosePreviewPane, prevSelectedThreadID, selectedThreadID])

  useLayoutEffect(() => {
    // keep selected tab in view when changing tabs
    if (!selectedItem || !isOpen) return

    const index = openItemInstances.findIndex(item => item.id === selectedItem)
    const tab = tabsRef.current[index]
    tab?.scrollIntoView({block: 'nearest', inline: 'nearest'})
  }, [selectedItem, openItemInstances, isOpen])

  useEffect(() => {
    // open resource panel if an eligible item is selected
    if (
      resourcePanelSupported &&
      selectedItem &&
      selectedItem !== previousSelectedItem &&
      !isResourcePanelOpen &&
      selectedItemInstance?.type === 'file' &&
      isFileReference(selectedItemInstance.reference)
    ) {
      openResourcePanel()
    }
  }, [
    isResourcePanelOpen,
    openResourcePanel,
    previousSelectedItem,
    resourcePanelSupported,
    selectedItem,
    selectedItemInstance,
  ])

  useEffect(() => {
    if (resourcePanelSupportedOverride !== undefined) return
    if (!optedInToPreviewFeatures) return
    if (selectedItem === previousSelectedItem) return
    if (!getChatState || !manager) return
    const {currentReferences, implicitReferences} = getChatState()
    // Automatically add the selected tab as an implicit ("ghost") reference. Supports any GitHub
    // primitive that can be opened in the workbench (files, issues, pull requests, ...).
    const reference =
      selectedItem && selectedItemInstance ? getImplicitReferenceForItem(selectedItemInstance) : undefined
    if (reference) {
      if (!currentReferences.some(ref => referencesAreEqual(ref, reference))) {
        if (implicitReferences.size > 0) {
          manager.clearImplicitReferences()
        }
        manager.addReference(reference, 'ContentPreview', {implicit: true})
      }
    } else if (implicitReferences.size > 0) {
      manager.clearImplicitReferences()
    }
  }, [
    getChatState,
    manager,
    optedInToPreviewFeatures,
    previousSelectedItem,
    resourcePanelSupportedOverride,
    selectedItem,
    selectedItemInstance,
  ])

  const downloadAll = async () => {
    const files = openFiles.map(item => new File([item.value], item.name))
    const blob = await downloadZip(files).blob()
    downloadFile(new File([blob], 'files.zip'))
  }

  const handleClose = useCallback(
    (closePreview = false) => {
      if (selectedItemInstance) {
        const shouldClosePreview = closePreview && openItemInstances.length === 1
        closeItem(selectedItemInstance.id)
        if (shouldClosePreview) {
          handleClosePreviewPane()
          sendEvent('dotcom_chat.activate', {target: 'BROWSER_CLOSE', mode: 'immersive'})
        }
      }
    },

    [closeItem, handleClosePreviewPane, openItemInstances.length, selectedItemInstance],
  )

  const tabsRef = useRef<Array<HTMLDivElement | null>>([])

  const tablistRef = useRef<HTMLDivElement>(null)
  useFocusZone(
    {
      containerRef: tablistRef,
      focusableElementFilter: el => el.role === 'tab',
      bindKeys: FocusKeys.ArrowHorizontal | FocusKeys.HomeAndEnd,
      focusInStrategy: 'first',
      focusOutBehavior: 'wrap',
    },
    [openItemInstances],
  )

  const closeTab = (i: number) => {
    const item = openItemInstances[i]
    if (!item) return

    // Remove implicit reference when its tab is closed before being promoted
    const closingReference = getImplicitReferenceForItem(item)
    if (closingReference && getChatState && manager) {
      const {implicitReferences} = getChatState()
      const implicitRef = [...implicitReferences].find(ref => referencesAreEqual(ref, closingReference))
      if (implicitRef) {
        manager.clearImplicitReferences()
      }
    }

    const nextFocusTab = tabsRef.current[i + 1] ?? tabsRef.current[i - 1] ?? tablistRef.current
    const nextFocusTarget = nextFocusTab?.querySelector('[role="tab"]') as HTMLElement | null
    nextFocusTarget?.focus()

    const isOnly = openItemInstances.length === 1
    closeItem(item.id)
    if (isOnly) handleClosePreviewPane()
    sendContentPreviewEvent('filePreviewTab.close', item)
  }

  const closeAll = () => {
    if (resourcePanelSupportedOverride === undefined && getChatState && manager) {
      const {implicitReferences} = getChatState()
      if (implicitReferences.size > 0) {
        manager.clearImplicitReferences()
      }
    }
    closeItems()
    handleClosePreviewPane()
    sendEvent('dotcom_chat.activate', {target: 'BROWSER_CLOSE_ALL_TABS', mode: 'immersive'})
  }

  return (
    <>
      <div className={clsx(styles.toolbar, styles.slimLineTopTabs)}>
        {resourcePanelSupported && !isResourcePanelOpen && (
          <div className={clsx(styles.actions, styles.rightDivider)}>
            <IconButton
              icon={ListUnorderedIcon}
              aria-label="Open resource panel"
              onClick={() => {
                openResourcePanel()
                sendEvent('dotcom_chat.activate', {
                  target: 'RESOURCE_PANEL_OPEN',
                  mode: 'immersive',
                  source: 'workbench_button',
                })
              }}
              variant="invisible"
            />
          </div>
        )}
        <span id={closeTabControlHintId} aria-hidden="true" className="sr-only">
          Press Delete to close the focussed tab.
        </span>
        <TabsContainer tabsListRef={tablistRef}>
          {openItemInstances.map((item, i) => (
            <ContentPreviewTab
              id={item.id === selectedItem ? activeTabId : undefined}
              tabPanelId={item.id === selectedItem ? selectedTabPanelId : undefined}
              controlHintId={closeTabControlHintId}
              key={item.id}
              isActive={item.id === selectedItem}
              isEphemeral={item.id === ephemeralTab}
              onClick={() => {
                startTransition(() => openItem(item.id))
                sendContentPreviewEvent('filePreviewTab.click', item)
              }}
              onDoubleClick={() => {
                const doubleClickedReference = getImplicitReferenceForItem(item)
                if (resourcePanelSupportedOverride === undefined && doubleClickedReference && getChatState && manager) {
                  const {implicitReferences} = getChatState()
                  const implicitRef = [...implicitReferences].find(ref =>
                    referencesAreEqual(ref, doubleClickedReference),
                  )
                  if (implicitRef) {
                    manager.promoteImplicitReference(implicitRef)
                    sendEvent('dotcom_chat.activate', {
                      target: 'IMPLICIT_REFERENCE_PROMOTED',
                      mode: 'immersive',
                      source: 'tab_double_click',
                    })
                  }
                }
                promoteTab(item.id)
              }}
              onClose={() => closeTab(i)}
              item={item}
              ref={el => {
                tabsRef.current[i] = el
              }}
            />
          ))}
        </TabsContainer>
        <div className={styles.actions}>
          {!disableActionsMenu && openItems.length > 0 && (
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton icon={KebabHorizontalIcon} aria-label="More options" variant="invisible" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>
                  {openFiles.length > 0 && (
                    <ActionList.Item onSelect={downloadAll}>
                      <ActionList.LeadingVisual>
                        <FileZipIcon />
                      </ActionList.LeadingVisual>
                      Download all files
                    </ActionList.Item>
                  )}
                  <ActionList.Item onSelect={closeAll}>
                    <ActionList.LeadingVisual>
                      <XIcon />
                    </ActionList.LeadingVisual>
                    Close all tabs
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
          <CloseContentPreviewButton {...props} hideExpandButton={hideExpandButton} />
        </div>
      </div>

      <div
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className={clsx(styles.content, 'content-preview-content')}
        role="tabpanel"
        id={selectedTabPanelId}
        aria-labelledby={activeTabId}
      >
        {showLoadingState ? (
          <LoadingState />
        ) : selectedItemInstance ? (
          <ContentPreviewItem isOpening={isOpening} previewItem={selectedItemInstance} onClose={handleClose} />
        ) : (
          <EmptyState isOpening={isOpening} hideIcebreakerSuggestion={hideIcebreakerSuggestion} />
        )}
      </div>
    </>
  )
}

function CloseContentPreviewButton(
  props: ContentPreviewExpansionProps & {
    hideExpandButton?: boolean
  },
) {
  const {hideExpandButton = false} = props
  const handleClosePreviewPane = useClosePreviewPane(props)
  const layoutContext = useOptionalLayoutContext()
  const secondaryColumnFullWidth = layoutContext?.secondaryColumnFullWidth ?? false
  const hostControlsExpansion = hasControlledContentPreviewExpansion(props)
  const workbenchExpanded = hostControlsExpansion ? props.isExpanded : secondaryColumnFullWidth
  const expansionSupported = hostControlsExpansion || Boolean(layoutContext)

  return (
    <div className={styles.closeActions}>
      {!hideExpandButton && expansionSupported && (
        <IconButton
          variant="invisible"
          icon={workbenchExpanded ? MinimizeIcon : MaximizeIcon}
          aria-label={workbenchExpanded ? 'Collapse workbench' : 'Expand workbench'}
          onClick={() => {
            if (hostControlsExpansion) {
              props.onExpandedChange(!props.isExpanded)
            } else {
              layoutContext?.toggleSecondaryColumnFullWidth()
            }
          }}
          className={hostControlsExpansion ? styles.onlyShowInSplitLayout : styles.onlyShowOnLargeViewports}
        />
      )}
      <IconButton
        variant="invisible"
        icon={XIcon}
        aria-label="Close workbench"
        onClick={() => {
          handleClosePreviewPane()
          sendEvent('dotcom_chat.activate', {target: 'CLOSE_PREVIEW_PANE_BUTTON', mode: 'immersive'})
        }}
      />
    </div>
  )
}

function ContentPreviewFallback(props: ContentPreviewExpansionProps) {
  return (
    <>
      <div className={styles.toolbar}>
        <div className={clsx(styles.actions, styles.rightDivider)}>
          <CloseContentPreviewButton {...props} />
        </div>
      </div>
      <div className={clsx(styles.content)}>
        <ErrorFallback regionName="The workbench" />
      </div>
    </>
  )
}

// We put as much logic into the Inner component as possible so that errors are always caught. We still need some
// container styles around the error boundary though to contain it if it does appear.
export function ContentPreview(props: ContentPreviewComponentProps) {
  const {className} = props
  const labelId = useId()
  const {contentPreviewRef} = useContentPreview()
  const contentPreviewOpen = useContentPreviewOpen()

  return (
    <div
      aria-labelledby={labelId}
      className={clsx(styles.container, className)}
      tabIndex={-1}
      data-testid="content-preview"
      role="dialog"
      aria-modal="false"
      ref={contentPreviewOpen ? contentPreviewRef : undefined}
    >
      <h2 id={labelId} className="sr-only">
        Workbench
      </h2>
      <ErrorBoundary boundaryName="copilot-content-preview" fallback={<ContentPreviewFallback {...props} />}>
        <ContentPreviewInner {...props} />
      </ErrorBoundary>
    </div>
  )
}

interface TabsContainerProps {
  tabsListRef: React.RefObject<HTMLDivElement | null>
  children: React.ReactNode
}

const TabsContainer = ({tabsListRef, children}: TabsContainerProps) => {
  return (
    <div className={styles.tabsScrollWrapper}>
      <ScrollContainer
        ref={tabsListRef}
        direction="horizontal"
        attributesForContentDiv={{
          className: styles.scrollContainerTabs,
          role: 'tablist',
          'aria-label': 'Workbench items',
        }}
      >
        {children}
      </ScrollContainer>
    </div>
  )
}
